import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Warning, CheckCircle, ArrowLeft } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ForgotPasswordForm = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      // In dev mode, backend returns the reset link
      if (response.data.dev_reset_link) {
        setDevLink(response.data.dev_reset_link);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-4" data-testid="reset-sent">
        <div className="flex items-start gap-2 p-4 bg-green-50 border border-green-200 text-green-700">
          <CheckCircle size={20} weight="fill" className="mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Check your email</p>
            <p>If that email exists in our system, we&apos;ve sent a reset link. It expires in 1 hour.</p>
          </div>
        </div>
        
        {devLink && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 text-xs">
            <p className="font-medium text-yellow-800 mb-1">Dev Mode - Email service not configured</p>
            <p className="text-yellow-700 mb-2">Use this link to reset:</p>
            <a href={devLink} className="text-[#0047FF] hover:underline break-all" data-testid="dev-reset-link">
              {devLink}
            </a>
          </div>
        )}
        
        <Button
          onClick={onBack}
          variant="outline"
          className="w-full border-[#E4E4E7]"
          data-testid="back-to-login-button"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Login
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <h3 className="font-heading text-lg font-bold text-[#09090B] mb-1">Reset your password</h3>
        <p className="text-sm text-[#71717A]">
          Enter your email and we&apos;ll send you a link to reset your password.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">
          <Warning size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="forgot-email">Email</Label>
        <Input
          id="forgot-email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="forgot-email-input"
          className="border-[#E4E4E7]"
        />
      </div>

      <div className="space-y-2">
        <Button
          type="submit"
          className="w-full bg-[#0047FF] hover:bg-[#0037CC] text-white"
          disabled={loading}
          data-testid="send-reset-button"
        >
          {loading ? 'Sending...' : 'Send Reset Link'}
        </Button>
        <Button
          type="button"
          onClick={onBack}
          variant="outline"
          className="w-full border-[#E4E4E7]"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Login
        </Button>
      </div>
    </form>
  );
};
